<template>
  <div class="baccarat-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.baccarat.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.baccarat.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <form v-else class="settings-form" @submit.prevent="saveConfig">

      <!-- Shoe Settings -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.baccarat.section_shoe') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.baccarat.label_deck_count') }}
            <select v-model.number="form.deck_count" class="form-select" required>
              <option :value="6">6 decks (312 cards)</option>
              <option :value="8">8 decks (416 cards) — Standard</option>
            </select>
            <span class="form-hint">More decks increase house edge slightly and reduce card counting effectiveness.</span>
          </label>
        </div>
      </section>

      <!-- Payout Rules -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.baccarat.section_payout') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.baccarat.label_commission') }}
            <input
              v-model.number="form.commission_pct"
              type="number"
              step="0.5"
              min="0"
              max="25"
              class="form-input"
              required
            />
            <span class="form-hint">Standard is 5%. Lower commission reduces house edge on Banker bets.</span>
          </label>

          <div class="edge-display">
            <div class="edge-row">
              <span class="edge-label">{{ $t('admin.config.baccarat.label_banker_edge') }}</span>
              <span class="edge-value banker">{{ bankerEdgeDisplay }}%</span>
            </div>
            <div class="edge-row">
              <span class="edge-label">{{ $t('admin.config.baccarat.label_player_edge') }}</span>
              <span class="edge-value player">{{ playerEdgeDisplay }}%</span>
            </div>
          </div>
        </div>

        <div class="form-row" style="margin-top: 14px;">
          <div class="form-label">
            {{ $t('admin.config.baccarat.label_tie_payout') }}
            <div class="radio-group">
              <label class="radio-label">
                <input v-model.number="form.tie_payout" type="radio" :value="8" class="radio-input" />
                <span>
                  8:1
                  <span class="rule-badge badge-warning">House edge: ~{{ tieEdge8Display }}%</span>
                </span>
              </label>
              <label class="radio-label">
                <input v-model.number="form.tie_payout" type="radio" :value="9" class="radio-input" />
                <span>
                  9:1
                  <span class="rule-badge badge-info">House edge: ~{{ tieEdge9Display }}%</span>
                </span>
              </label>
            </div>
            <span class="form-hint">Tie occurs ~9.52% of hands. Higher payout reduces house edge.</span>
          </div>
        </div>
      </section>

      <!-- Side Bets -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.baccarat.section_side_bets') }}</h3>

        <p class="form-hint" style="margin-bottom: 12px;">
          Pair bets pay 11:1 if the first two cards of the respective hand share the same rank.
          House edge ≈ 10.4% (8-deck).
        </p>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.player_pair_enabled" type="checkbox" class="toggle-checkbox" />
            <span>
              Player Pair — 11:1 if Player's first two cards match
              <span v-if="form.player_pair_enabled" class="rule-badge badge-info">Enabled</span>
              <span v-else class="rule-badge badge-disabled">Disabled</span>
            </span>
          </label>
        </div>

        <div class="form-row" style="margin-top: 10px;">
          <label class="toggle-label">
            <input v-model="form.banker_pair_enabled" type="checkbox" class="toggle-checkbox" />
            <span>
              Banker Pair — 11:1 if Banker's first two cards match
              <span v-if="form.banker_pair_enabled" class="rule-badge badge-info">Enabled</span>
              <span v-else class="rule-badge badge-disabled">Disabled</span>
            </span>
          </label>
        </div>
      </section>

      <!-- Scoreboards & Roads -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.baccarat.section_scoreboard') }}</h3>

        <p class="form-hint" style="margin-bottom: 12px;">
          Baccarat scoreboards help players track betting patterns. Bead Road and Big Road are
          standard. Big Eye Boy and Small Road are derived predictive roads.
        </p>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.bead_road_enabled" type="checkbox" class="toggle-checkbox" />
            <span>Bead Road — sequential record of results (P/B/T)</span>
          </label>
        </div>

        <div class="form-row" style="margin-top: 10px;">
          <label class="toggle-label">
            <input v-model="form.big_road_enabled" type="checkbox" class="toggle-checkbox" />
            <span>Big Road — columnar streaks display (Banker/Player shoes)</span>
          </label>
        </div>

        <div class="form-row" style="margin-top: 10px;">
          <label class="toggle-label">
            <input v-model="form.big_eye_boy_enabled" type="checkbox" class="toggle-checkbox" />
            <span>
              Big Eye Boy — derived predictive road
              <span class="rule-badge badge-disabled">Optional</span>
            </span>
          </label>
        </div>

        <div class="form-row" style="margin-top: 10px;">
          <label class="toggle-label">
            <input v-model="form.small_road_enabled" type="checkbox" class="toggle-checkbox" />
            <span>
              Small Road — derived predictive road
              <span class="rule-badge badge-disabled">Optional</span>
            </span>
          </label>
        </div>
      </section>

      <!-- History & Features -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.baccarat.section_history') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.baccarat.label_history_length') }}
            <input
              v-model.number="form.history_length"
              type="number"
              step="10"
              min="10"
              max="500"
              class="form-input"
              required
            />
            <span class="form-hint">Max rounds retained in scoreboards. Default: 100.</span>
          </label>
        </div>

        <div class="form-row" style="margin-top: 14px;">
          <label class="toggle-label">
            <input v-model="form.squeeze_enabled" type="checkbox" class="toggle-checkbox" />
            <span>
              {{ $t('admin.config.baccarat.label_squeeze') }}
              <span v-if="form.squeeze_enabled" class="rule-badge badge-info">Enabled</span>
              <span v-else class="rule-badge badge-disabled">Disabled</span>
            </span>
          </label>
        </div>
        <p class="form-hint" style="margin-top: 4px;">
          Gradually reveals cards for suspense. Players can enable/disable per session.
          Enabling here makes the feature available; players still control their preference.
        </p>
      </section>

      <!-- Bet Limits -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>

        <p class="form-hint" style="margin-bottom: 12px;">
          {{ $t('admin.config.baccarat.hint_bet_limits') }}
        </p>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.baccarat.label_min_main_bet') }}
            <input
              v-model.number="form.min_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.baccarat.label_max_main_bet') }}
            <input
              v-model.number="form.max_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
        </div>

        <div class="form-row" style="margin-top: 12px;">
          <label class="form-label">
            {{ $t('admin.config.baccarat.label_min_pair_bet') }}
            <input
              v-model.number="form.min_pair_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.baccarat.label_max_pair_bet') }}
            <input
              v-model.number="form.max_pair_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
        </div>
      </section>

      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
      <div v-if="saveSuccess" class="success-banner">{{ saveSuccess }}</div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
        </button>
        <span v-if="sourceLabel" class="source-badge" :class="sourceClass">
          {{ sourceLabel }}
        </span>
        <div class="rtp-inline">
          <span class="rtp-label">{{ $t('admin.config.baccarat.label_banker_rtp') }}</span>
          <span class="rtp-value">{{ rtp }}%</span>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

interface BaccaratConfigResponse {
  source: 'database' | 'default'
  deck_count: number
  commission_pct: number
  tie_payout: number
  player_pair_enabled: boolean
  banker_pair_enabled: boolean
  bead_road_enabled: boolean
  big_road_enabled: boolean
  big_eye_boy_enabled: boolean
  small_road_enabled: boolean
  history_length: number
  squeeze_enabled: boolean
  min_bet: string
  max_bet: string
  min_pair_bet: string
  max_pair_bet: string
  banker_edge: number
  player_edge: number
  tie_edge: number
  rtp: number
}

const { t } = useI18n()

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveSuccess = ref('')

const source = ref<'database' | 'default'>('default')
const rtp = ref(98.94)
const bankerEdge = ref(1.06)
const playerEdge = ref(1.24)
const tieEdge = ref(14.36)

// Hardcoded tie edge values for visual reference in the radio labels
const tieEdge8Display = '14.36'
const tieEdge9Display = '4.84'

const form = reactive({
  deck_count: 8 as 6 | 8,
  commission_pct: 5.0,
  tie_payout: 8 as 8 | 9,
  player_pair_enabled: true,
  banker_pair_enabled: true,
  bead_road_enabled: true,
  big_road_enabled: true,
  big_eye_boy_enabled: false,
  small_road_enabled: false,
  history_length: 100,
  squeeze_enabled: false,
  min_bet: '1.00',
  max_bet: '1000.00',
  min_pair_bet: '1.00',
  max_pair_bet: '500.00',
})

const sourceLabel = computed(() => (source.value === 'database' ? t('admin.config.saved_to_db') : t('admin.config.using_defaults')))
const sourceClass = computed(() => (source.value === 'database' ? 'source-db' : 'source-default'))
const bankerEdgeDisplay = computed(() => bankerEdge.value.toFixed(4))
const playerEdgeDisplay = computed(() => playerEdge.value.toFixed(4))

async function loadConfig() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<BaccaratConfigResponse>('/admin/api/games/baccarat/config')
    populateForm(res.data)
  } catch {
    loadError.value = t('admin.config.baccarat.error_load')
  } finally {
    loading.value = false
  }
}

function populateForm(data: BaccaratConfigResponse) {
  source.value = data.source
  rtp.value = data.rtp
  bankerEdge.value = data.banker_edge
  playerEdge.value = data.player_edge
  tieEdge.value = data.tie_edge
  form.deck_count = data.deck_count as 6 | 8
  form.commission_pct = data.commission_pct
  form.tie_payout = data.tie_payout as 8 | 9
  form.player_pair_enabled = data.player_pair_enabled
  form.banker_pair_enabled = data.banker_pair_enabled
  form.bead_road_enabled = data.bead_road_enabled
  form.big_road_enabled = data.big_road_enabled
  form.big_eye_boy_enabled = data.big_eye_boy_enabled
  form.small_road_enabled = data.small_road_enabled
  form.history_length = data.history_length
  form.squeeze_enabled = data.squeeze_enabled
  form.min_bet = data.min_bet
  form.max_bet = data.max_bet
  form.min_pair_bet = data.min_pair_bet
  form.max_pair_bet = data.max_pair_bet
}

async function saveConfig() {
  saveError.value = ''
  saveSuccess.value = ''
  saving.value = true

  try {
    const res = await apiClient.post<BaccaratConfigResponse>('/admin/api/games/baccarat/config', {
      deck_count: form.deck_count,
      commission_pct: form.commission_pct,
      tie_payout: form.tie_payout,
      player_pair_enabled: form.player_pair_enabled,
      banker_pair_enabled: form.banker_pair_enabled,
      bead_road_enabled: form.bead_road_enabled,
      big_road_enabled: form.big_road_enabled,
      big_eye_boy_enabled: form.big_eye_boy_enabled,
      small_road_enabled: form.small_road_enabled,
      history_length: form.history_length,
      squeeze_enabled: form.squeeze_enabled,
      min_bet: form.min_bet,
      max_bet: form.max_bet,
      min_pair_bet: form.min_pair_bet,
      max_pair_bet: form.max_pair_bet,
    })
    populateForm(res.data)
    saveSuccess.value = t('admin.config.baccarat.save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.baccarat.error_save')
  } finally {
    saving.value = false
  }
}

onMounted(loadConfig)
</script>

<style scoped>
.baccarat-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* Form */
.settings-form {
  max-width: 720px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: flex-start;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input,
.form-select {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

/* Radio group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
}

.radio-input {
  accent-color: var(--accent, #1a9e6e);
  width: 15px;
  height: 15px;
}

/* Edge display */
.edge-display {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  justify-content: center;
  min-width: 120px;
  padding-top: 18px;
}

.edge-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edge-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
  min-width: 72px;
}

.edge-value {
  font-size: 0.9rem;
  font-weight: 700;
}

.edge-value.banker {
  color: var(--color-danger-light, #f87171);
}

.edge-value.player {
  color: var(--color-info-light, #60a5fa);
}

/* Rule badge */
.rule-badge {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  margin-left: 6px;
}

.badge-warning {
  background: rgba(251, 191, 36, 0.15);
  color: var(--color-warning-light, #fbbf24);
}

.badge-info {
  background: rgba(96, 165, 250, 0.15);
  color: var(--color-info-light, #60a5fa);
}

.badge-disabled {
  background: rgba(107, 114, 128, 0.15);
  color: var(--color-muted, #9ca3af);
}

/* Toggle */
.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
  flex: 1;
  min-width: 200px;
}

.toggle-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--accent, #1a9e6e);
  flex-shrink: 0;
}

.toggle-checkbox:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Actions row */
.form-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
  flex-wrap: wrap;
}

/* Source badge */
.source-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

/* RTP inline */
.rtp-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.rtp-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-success-light, #34d399);
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}
</style>
